import React, { useEffect, useState } from 'react';
import { url } from '../../../util/config';
import './CreateSequenceStyles.css';

const placeholderRegex = /{{\s*([^}]+)\s*}}/g;

const TemplatePicker = ({ onClose, onConfirm, headerFields = [] }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [bindings, setBindings] = useState({});

  useEffect(() => {
    let mounted = true;
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${url}/template`, {
          headers: { Authorization: 'Bearer ' + token },
        });
        const data = await res.json();
        if (mounted) setTemplates(Array.isArray(data) ? data : []);
      } catch (e) {
        if (mounted) setTemplates([]);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchTemplates();
    return () => { mounted = false; };
  }, []);

  const computePlaceholders = (body = '') => {
    const set = new Set();
    let m;
    while ((m = placeholderRegex.exec(body)) !== null) {
      set.add(m[1].trim());
    }
    return Array.from(set);
  };

  useEffect(() => {
    if (selected) {
      const ph = computePlaceholders(selected.template_message_body || '');
      const map = {};
      ph.forEach(p => { map[p] = map[p] || ''; });
      setBindings(map);
    } else {
      setBindings({});
    }
  }, [selected]);

  const handleBind = (placeholder, value) => {
    setBindings(prev => ({ ...prev, [placeholder]: value }));
  };

  const allBound = Object.keys(bindings).length === 0 || Object.values(bindings).every(v => v && v !== '');

  return (
    <div className="template-picker-overlay">
      <div className="template-picker-modal">
        <div className="template-picker-header">
          <div className="left-controls">
            <button className="btn btn-link ps-0" onClick={onClose}>Back</button>
          </div>
          <h5 className="m-0 flex-grow-1 text-center">Select Template</h5>
          <div className="right-controls" />
        </div>

        <div className="template-picker-body">
          {loading ? (
            <div className="text-center py-6">Loading templates...</div>
          ) : (
            <div className="picker-grid-wrap">
              {/* LEFT: templates grid */}
              <div className="picker-grid">
                {templates.length === 0 && <div className="text-muted p-3">No templates found</div>}
                <div className="template-grid">
                  {templates.map((t, idx) => {
                    const placeholdersCount = computePlaceholders(t.template_message_body || '').length;
                    return (
                      <div
                        key={t._id || idx}
                        className={`template-card ${selected === t ? 'selected' : ''}`}
                        onClick={() => setSelected(t)}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="template-card-media">
                          {t.template_media_url ? (
                            <img
                              src={t.template_media_url}
                              alt={t.template_name || t.title || 'media'}
                              className="template-media-img"
                            />
                          ) : t.template_type === 'Carousel' && (t.carousel || []).length > 0 ? (
                            <div className="carousel-scroll">
                              {(t.carousel || []).map((it, i) => (
                                <div key={i} className="carousel-thumb">{i + 1}</div>
                              ))}
                            </div>
                          ) : (
                            <div className="media-placeholder small">No media</div>
                          )}
                        </div>

                        <div className="template-card-body">
                          <div className="template-title">{t.template_name || t.name || t.title || 'Untitled'}</div>
                          <div className="template-sub text-muted">
                            {t.template_message_body ? `${placeholdersCount} placeholder${placeholdersCount !== 1 ? 's' : ''}` : 'No placeholders'}
                          </div>
                        </div>

                        <div className="template-card-footer">
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={(e) => { e.stopPropagation(); setSelected(t); }}
                          >
                            Select
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* RIGHT: selected template & mapping (sticky) */}
              <aside className="picker-selected-panel">
                <div className="template-picker-selected">
                  <div className="selected-header d-flex justify-content-between align-items-center">
                    <strong>Selected</strong>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSelected(null); setBindings({}); }}>Clear</button>
                  </div>

                  {!selected ? (
                    <div className="text-muted mt-4">No template selected</div>
                  ) : (
                    <>
                      <div className="selected-preview mt-3">
                        {selected.template_media_url && (
                          <img src={selected.template_media_url} alt="preview" className="selected-media mb-2" />
                        )}
                        <div className="preview-title fw-bold">{selected.template_name || selected.name || selected.title}</div>
                        <div className="preview-body text-muted small">{selected.template_message_body || '—'}</div>
                        {selected.button_json && selected.button_json.length > 0 && (
                          <div className="mt-3">
                            {selected.button_json.map((btn, i) => (
                              <div key={i} className="small mb-1 badge bg-light text-dark">{btn.button || btn.content || btn.type}</div>
                            ))}
                          </div>
                        )}
                      </div>

                      {computePlaceholders(selected.template_message_body || '').length > 0 && (
                        <div className="placeholder-bindings mt-3">
                          <div className="mb-2 text-muted">Map placeholders</div>
                          {computePlaceholders(selected.template_message_body || '').map(ph => (
                            <div className="binding-row" key={ph}>
                              <label className="binding-label">{ph}</label>
                              <select
                                className="form-select"
                                value={bindings[ph] || ''}
                                onChange={e => handleBind(ph, e.target.value)}
                              >
                                <option value="">-- select field --</option>
                                {headerFields.map((hf, i) => <option key={hf + i} value={hf}>{hf}</option>)}
                              </select>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="d-flex justify-content-end mt-4">
                        <button className="btn btn-secondary me-2" onClick={() => { setSelected(null); setBindings({}); }}>Reset</button>
                        <button
                          className="btn btn-success"
                          disabled={!allBound || !selected}
                          onClick={() => onConfirm(selected, bindings)}
                        >
                          Confirm
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </aside>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplatePicker;